// src/pages/ProductsPage.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Search } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { translations } from '../utils/translations';
import ProductTable from '../components/admin/ProductTable';
import ProductDialog from '../components/admin/ProductDialog';
import InventoryDialog from '../components/admin/InventoryDialog';
import { productApi } from '../api';

const ProductsPage = () => {
  const { language, currentUser } = useApp();
  const t = translations[language];

  // 只有总店管理员可以新增/编辑/删除商品
  const isHQ = currentUser?.store_type === 'headquarters';

  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [showInventoryDialog, setShowInventoryDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  const loadProducts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await productApi.getList({ status: 'active' });
      if (res.success) {
        setProducts(res.data);
        setFilteredProducts(res.data);
      } else {
        setError(res.message || '获取商品失败');
      }
    } catch (err) {
      setError(err.message || '获取商品失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadProducts(); }, []);

  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
      return;
    }
    const term = searchTerm.toLowerCase();
    setFilteredProducts(products.filter(p =>
      p.name?.toLowerCase().includes(term) ||
      p.name_en?.toLowerCase().includes(term) ||
      p.name_km?.toLowerCase().includes(term) ||
      p.barcode?.includes(term)
    ));
  }, [searchTerm, products]);

  const handleAdd = () => {
    setEditingProduct(null);
    setShowDialog(true);
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowDialog(true);
  };

  const handleSubmit = async (productData) => {
    try {
      if (editingProduct) {
        const res = await productApi.update(editingProduct.id, productData);
        if (res.success) {
          loadProducts();
          setShowDialog(false);
          setEditingProduct(null);
          alert(language === 'en' ? 'Product updated successfully' : '商品更新成功！');
        } else {
          alert(res.message || '更新失败');
        }
      } else {
        const res = await productApi.create(productData);
        if (res.success) {
          loadProducts();
          setShowDialog(false);
          alert(language === 'en' ? 'Product added successfully' : '商品添加成功！');
        } else {
          alert(res.message || '添加失败');
        }
      }
    } catch (err) {
      alert(err.message || '操作失败，请重试');
    }
  };

  const handleDelete = async (productId) => {
    const product = products.find(p => p.id === productId);
    if (!window.confirm(`确定要删除商品"${product?.name}"吗？`)) return;
    try {
      const res = await productApi.delete(productId);
      if (res.success) {
        setProducts(products.filter(p => p.id !== productId));
        alert(language === 'en' ? 'Product deleted successfully' : '删除成功！');
      } else {
        alert(res.message || '删除失败');
      }
    } catch (err) {
      alert(err.message || '删除失败，请重试');
    }
  };

  const handleInventory = (product) => {
    setSelectedProduct(product);
    setShowInventoryDialog(true);
  };

  return (
    <div className="h-full flex flex-col gap-6">
      {/* 头部 */}
      <div className="flex-shrink-0 flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">{t.products}</h2>
          <p className="text-sm text-gray-500 mt-1">
            共 {filteredProducts.length} 个商品
            {!isHQ && <span className="ml-2 text-orange-500 text-xs">（只读，商品由总店管理）</span>}
          </p>
        </div>

        <div className="flex flex-col md:flex-row gap-3">
          {/* 搜索框 */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={language === 'en' ? 'Search products...' : '搜索商品...'}
              className="pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 w-full md:w-64"
            />
          </div>

          {/* 新增按钮：仅总店管理员可见 */}
          {isHQ && (
            <button
              onClick={handleAdd}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              {t.addProduct}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="flex-shrink-0 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg">
          {error}
          <button onClick={loadProducts} className="ml-4 underline">重试</button>
        </div>
      )}

      {/* 表格 */}
      <div className="flex-1 min-h-0 bg-white rounded-xl shadow flex flex-col">
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">加载中...</p>
            </div>
          </div>
        ) : (
          <div className="flex-1 overflow-auto">
            <ProductTable
              products={filteredProducts}
              onEdit={isHQ ? handleEdit : null}
              onDelete={isHQ ? handleDelete : null}
              onInventory={handleInventory}
              isReadOnly={!isHQ}
            />
          </div>
        )}
      </div>

      {showDialog && isHQ && (
        <ProductDialog
          product={editingProduct}
          onClose={() => { setShowDialog(false); setEditingProduct(null); }}
          onSubmit={handleSubmit}
        />
      )}

      {showInventoryDialog && selectedProduct && (
        <InventoryDialog
          product={selectedProduct}
          onClose={() => { setShowInventoryDialog(false); setSelectedProduct(null); }}
          onSuccess={loadProducts}
        />
      )}
    </div>
  );
};

export default ProductsPage;